import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { RefreshCw, Share2, Sparkles, Heart, Briefcase, Home, Users } from "lucide-react";
import { Button } from "./ui/button";
import { AskWheel } from "./AskWheel";
import { ShareModal } from "./ShareModal";
import { askPairWheel } from "../lib/api";

const RTYPE_META = {
  romantic: { label: "Romantic",  Icon: Heart },
  business: { label: "Business",  Icon: Briefcase },
  family:   { label: "Family",    Icon: Home },
  friends:  { label: "Friendship", Icon: Users },
};

const CHAPTER_LABEL = {
  chemistry: "Chemistry",
  communication: "Communication",
  conflict: "Conflict style",
  intimacy: "Intimacy",
  longevity: "Longevity",
  forecast: "Twelve months ahead",
  decision_style: "Decision style",
  risk_and_money: "Risk and money",
  complementary_skills: "Complementary skills",
  growth: "Growth",
  emotional_patterns: "Emotional patterns",
  boundaries: "Boundaries",
  rituals: "Rituals and home",
  shared_rhythm: "Shared rhythm",
  loyalty: "Loyalty style",
  repair: "Repair after friction",
  adventure: "Adventure and play",
};

const scoreTone = (score) => {
  if (score >= 84) return { ring: "ring-emerald-400/60", text: "text-emerald-300" };
  if (score >= 72) return { ring: "ring-stardust/70",    text: "text-stardust" };
  if (score >= 60) return { ring: "ring-amber-400/60",   text: "text-amber-300" };
  if (score >= 48) return { ring: "ring-orange-400/60",  text: "text-orange-300" };
  return { ring: "ring-rose-400/60", text: "text-rose-300" };
};

const PersonBadge = ({ p, side }) => (
  <div className={`flex flex-col items-center text-center ${side === "b" ? "sm:items-start" : "sm:items-end"}`}>
    <div className="zodiac-orb mb-2 animate-float-slow" style={{ width: "88px", height: "88px", fontSize: "2.4rem" }}>
      <span style={{ fontFamily: "'DM Serif Display', serif" }}>{p.zodiac_symbol + "\uFE0E"}</span>
    </div>
    <p className="font-display text-2xl sm:text-3xl text-[#F5EBD8] leading-tight">{p.name}</p>
    <p className="text-stardust/80 uppercase tracking-[0.3em] text-[10px] mt-1">{p.zodiac_sign}</p>
    <p className="text-slate-500 text-[10px] uppercase tracking-[0.25em] mt-1">{p.element} · Ruled by {p.ruling_planet}</p>
  </div>
);

export const PairResults = ({ data, onRestart, readOnly = false }) => {
  const [shareOpen, setShareOpen] = useState(false);
  const [thread, setThread] = useState(() => (Array.isArray(data?.questions) ? data.questions : []));
  if (!data) return null;

  const meta = RTYPE_META[data.relationship_type] || RTYPE_META.romantic;
  const RIcon = meta.Icon;
  const tone = scoreTone(data.harmony.score);

  const askContext = {
    person_a: {
      name: data.person_a.name,
      date_of_birth: data.person_a.date_of_birth,
      time_of_birth: data.person_a.time_of_birth || null,
      place_of_birth: data.person_a.place_of_birth,
    },
    person_b: {
      name: data.person_b.name,
      date_of_birth: data.person_b.date_of_birth,
      time_of_birth: data.person_b.time_of_birth || null,
      place_of_birth: data.person_b.place_of_birth,
    },
    relationship_type: data.relationship_type,
    harmony: data.harmony,
    synopsis: data.synopsis,
    chapters: data.chapters,
  };

  const pairSharePayload = {
    person_a: data.person_a,
    person_b: data.person_b,
    relationship_type: data.relationship_type,
    harmony: data.harmony,
    synopsis: data.synopsis,
    chapters: data.chapters,
    chapter_order: data.chapter_order,
  };

  const showAsk = !readOnly || thread.length > 0;

  return (
    <section
      data-testid="pair-results-section"
      className="relative min-h-screen px-4 sm:px-6 pt-24 sm:pt-28 pb-20 max-w-6xl mx-auto"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-center mb-10 sm:mb-14"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-stardust/[0.06] border border-stardust/20 text-stardust/90 text-[10px] uppercase tracking-[0.3em] mb-6" data-testid="pair-rtype-chip">
          <RIcon className="h-3 w-3" />
          {meta.label} pair
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-6 sm:gap-10 mb-10">
          <PersonBadge p={data.person_a} side="a" />
          <div className="flex flex-col items-center">
            <div className={`relative h-24 w-24 sm:h-28 sm:w-28 rounded-full flex items-center justify-center ring-2 ${tone.ring} bg-cosmic-800/60 backdrop-blur`}>
              <span className={`font-display text-3xl sm:text-4xl ${tone.text}`} data-testid="pair-harmony-score">
                {data.harmony.score}
              </span>
              <span className="absolute -bottom-6 text-[9px] uppercase tracking-[0.3em] text-slate-500">out of 100</span>
            </div>
            <p className="mt-10 text-stardust uppercase tracking-[0.35em] text-[10px] sm:text-[11px]" data-testid="pair-harmony-label">{data.harmony.label}</p>
          </div>
          <PersonBadge p={data.person_b} side="b" />
        </div>

        <p
          data-testid="pair-synopsis"
          className="font-serif-italic text-slate-300 text-base sm:text-2xl max-w-3xl mx-auto leading-relaxed px-2"
        >
          {data.synopsis}
        </p>
      </motion.div>

      {/* Dynamics */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12"
      >
        {[
          { l: "Element dynamic",  v: data.harmony.element_dynamic },
          { l: "Modality dynamic", v: data.harmony.modality_dynamic },
          { l: "Planet dynamic",   v: data.harmony.planet_dynamic },
        ].map((d) => (
          <div key={d.l} className="rounded-xl bg-white/[0.02] border border-white/10 p-5">
            <p className="text-stardust/70 uppercase tracking-[0.25em] text-[10px] mb-2">{d.l}</p>
            <p className="text-slate-200 text-sm leading-relaxed">{d.v}</p>
          </div>
        ))}
      </motion.div>

      {/* Chapters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5 auto-rows-[minmax(160px,auto)]">
        {(data.chapter_order || []).map((key, idx) => {
          const ch = data.chapters?.[key];
          if (!ch) return null;
          const span = key === "forecast"
            ? "md:col-span-6"
            : idx % 3 === 0
              ? "md:col-span-4"
              : "md:col-span-3";
          return (
            <motion.article
              key={key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 * idx }}
              className={`bento-card p-5 sm:p-7 flex flex-col ${span}`}
              data-testid={`pair-chapter-${key}`}
            >
              <div className="flex items-center gap-3 mb-3 sm:mb-4">
                <div className="h-8 w-8 rounded-full bg-stardust/10 border border-stardust/30 flex items-center justify-center shrink-0">
                  <Sparkles className="h-3.5 w-3.5 text-stardust" />
                </div>
                <span className="text-stardust/80 uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[9px] sm:text-[10px]">
                  {CHAPTER_LABEL[key] || key}
                </span>
              </div>

              <h3 className="font-display text-xl sm:text-3xl text-[#F5EBD8] mb-3 leading-tight">{ch.title}</h3>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4 sm:mb-5">{ch.summary}</p>

              {ch.insights?.length > 0 && (
                <ul className="space-y-2 mt-auto">
                  {ch.insights.map((line, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-400 text-[13px] sm:text-sm leading-relaxed">
                      <span className="text-stardust mt-0.5 sm:mt-1 shrink-0">✦</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.article>
          );
        })}
      </div>

      {/* Ask the wheel (pair) */}
      {showAsk && (
        <AskWheel
          context={askContext}
          thread={thread}
          onThreadChange={setThread}
          readOnly={readOnly}
          askFn={askPairWheel}
          mode="pair"
        />
      )}

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="flex flex-col items-center mt-20 sm:mt-24 gap-4"
      >
        <Sparkles className="h-5 w-5 text-stardust mb-2 animate-twinkle" />
        {!readOnly ? (
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Button
              data-testid="pair-results-share-button"
              onClick={() => setShareOpen(true)}
              className="bg-stardust hover:bg-stardust-light text-cosmic-900 rounded-md uppercase tracking-[0.3em] text-xs px-8 py-6 font-medium shadow-[0_0_28px_rgba(226,194,117,0.25)] hover:shadow-[0_0_44px_rgba(226,194,117,0.45)] transition-all duration-300"
            >
              <Share2 className="h-4 w-4 mr-3" />
              Share this pair reading
            </Button>
            <Button
              data-testid="pair-results-restart-button"
              onClick={onRestart}
              variant="outline"
              className="border-stardust/40 text-stardust hover:bg-stardust hover:text-cosmic-900 hover:border-stardust rounded-md uppercase tracking-[0.3em] text-xs px-8 py-6 transition-all duration-300"
            >
              <RefreshCw className="h-4 w-4 mr-3" />
              New reading
            </Button>
          </div>
        ) : (
          <Link
            to="/"
            data-testid="pair-shared-cta-button"
            className="inline-flex items-center bg-stardust hover:bg-stardust-light text-cosmic-900 rounded-md uppercase tracking-[0.3em] text-xs px-10 py-5 font-medium shadow-[0_0_30px_rgba(226,194,117,0.3)] transition-all duration-300"
          >
            <Sparkles className="h-4 w-4 mr-3" />
            Get our own reading
          </Link>
        )}
        <p className="text-slate-500 text-xs uppercase tracking-[0.25em] mt-4 text-center px-6">
          For reflection, not prescription · <Link to="/legal" className="hover:text-stardust transition-colors">Privacy &amp; legal</Link>
        </p>
      </motion.div>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        reading={data}
        thread={thread}
        mode="pair"
        pairPayload={pairSharePayload}
      />
    </section>
  );
};
